/**
 * RUNYI universal UI shell registry.
 *
 * This registers RUNYI-owned navigation surfaces, not provider resources.
 * Marketplace/provider/model/resource records stay backend-authoritative. New
 * applications and services use their existing governed registration and
 * product-scoped screen; they do not acquire OWNER ROOT access by appearing in UI.
 */
import { AppWindow, Database, HardDrive, Plus, Settings2, type LucideIcon } from "lucide-react";

export type EnterpriseRoute =
  | "home" | "ai" | "files" | "applications" | "infrastructure"
  | "operations" | "more" | "new" | "catalogue" | "bridge"
  | "flow" | "movies" | "governance";

export type WorkspaceSearchScope =
  | "UNIVERSAL_AUTHORIZED" | "MARKETPLACE" | "NEW_INFRASTRUCTURE"
  | "APPLICATIONS_PROJECTS" | "EXPLORER_AUTHORIZED" | "SETTINGS"
  | "AI_WORKSPACE" | "CONTEXTUAL";

export type EnterpriseWorkspaceDefinition = {
  route: EnterpriseRoute;
  title: string;
  description: string;
  primaryRoute: "infrastructure" | "new" | "applications" | "files" | "governance" | null;
  searchScope: WorkspaceSearchScope;
  focal: boolean;
  viewportMode?: "IMMERSIVE" | "SCROLL";
  bottomNavigation: boolean;
  mobileLabel?: string;
  icon?: LucideIcon;
};

export const enterpriseWorkspaces: readonly EnterpriseWorkspaceDefinition[] = [
  { route: "infrastructure", title: "Marketplace", description: "Discover and assign governed universal resources and services.", primaryRoute: "infrastructure", searchScope: "MARKETPLACE", focal: true, bottomNavigation: true, mobileLabel: "Mrk", icon: Database },
  { route: "new", title: "New Infrastructure", description: "Acquire and connect infrastructure through backend-defined operations.", primaryRoute: "new", searchScope: "NEW_INFRASTRUCTURE", focal: true, bottomNavigation: true, mobileLabel: "New", icon: Plus },
  { route: "applications", title: "Applications & Projects", description: "Configure, operate, open and test registered products.", primaryRoute: "applications", searchScope: "APPLICATIONS_PROJECTS", focal: true, bottomNavigation: true, mobileLabel: "Apps", icon: AppWindow },
  { route: "files", title: "Workspace Explorer", description: "Explore the currently authorized files, storage and databases.", primaryRoute: "files", searchScope: "EXPLORER_AUTHORIZED", focal: true, viewportMode: "IMMERSIVE", bottomNavigation: true, mobileLabel: "Exp", icon: HardDrive },
  { route: "governance", title: "Enterprise Settings", description: "Manage identity, appearance and governed configuration.", primaryRoute: "governance", searchScope: "SETTINGS", focal: true, bottomNavigation: true, mobileLabel: "Sett", icon: Settings2 },
  { route: "ai", title: "AI Workspace", description: "Provider-neutral RUNYI Chat and Work, entered from the header.", primaryRoute: null, searchScope: "AI_WORKSPACE", focal: true, viewportMode: "IMMERSIVE", bottomNavigation: false },
  { route: "catalogue", title: "Marketplace", description: "Available provider capabilities.", primaryRoute: "infrastructure", searchScope: "MARKETPLACE", focal: true, bottomNavigation: false },
  { route: "movies", title: "Movies Engine", description: "Independently scoped application workspace.", primaryRoute: "applications", searchScope: "CONTEXTUAL", focal: true, bottomNavigation: false },
  { route: "home", title: "Enterprise Home", description: "Enterprise starting surface.", primaryRoute: null, searchScope: "UNIVERSAL_AUTHORIZED", focal: false, bottomNavigation: false },
  { route: "operations", title: "Operations", description: "Governed enterprise operations.", primaryRoute: null, searchScope: "CONTEXTUAL", focal: true, bottomNavigation: false },
  { route: "more", title: "More", description: "Authorized function directory.", primaryRoute: null, searchScope: "UNIVERSAL_AUTHORIZED", focal: true, bottomNavigation: false },
  { route: "bridge", title: "Connections", description: "Provider connections and adopted infrastructure.", primaryRoute: null, searchScope: "CONTEXTUAL", focal: true, bottomNavigation: false },
  { route: "flow", title: "Flow", description: "Governed execution and verification.", primaryRoute: null, searchScope: "CONTEXTUAL", focal: true, bottomNavigation: false },
] as const;

export const primaryEnterpriseWorkspaces = enterpriseWorkspaces.filter((item) => item.bottomNavigation);
export const allEnterpriseRoutes = enterpriseWorkspaces.map((item) => item.route);

export function enterpriseWorkspace(route: EnterpriseRoute): EnterpriseWorkspaceDefinition {
  const item = enterpriseWorkspaces.find((workspace) => workspace.route === route);
  if (!item) throw new Error(`RUNYI workspace is not registered: ${route}`);
  return item;
}
